import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";

import styles from "./demo.module.scss";
import { askDemo, fetchDemo, panelKey, trackDemo } from "./demoService";
import { BRAND, OFFER, PRODUCT_URL, campaignUrl, sourceLabel } from "./offer";

export const cx = (...names) => names.filter(Boolean).map((name) => styles[name] || name).join(" ");

export const CHANNELS = [
  { id: "website", label: "Website" },
  { id: "instagram", label: "Instagram" },
  { id: "whatsapp", label: "WhatsApp" },
];

const stamp = () => new Date().toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });

function useClock() {
  const [now, setNow] = useState(stamp());
  useEffect(() => {
    const timer = setInterval(() => setNow(stamp()), 1000);
    return () => clearInterval(timer);
  }, []);
  return now;
}

const reducedMotion = () => !!window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

// Types example questions into the composer's placeholder, so the box reads as somewhere to type.
function useTypedPlaceholder(lines, active) {
  const [text, setText] = useState(lines[0]);
  useEffect(() => {
    setText(lines[0]);
    if (!active || lines.length < 2 || reducedMotion()) return undefined;
    let line = 0;
    let pos = lines[0].length;
    let deleting = true;
    let timer;
    const tick = () => {
      if (deleting) {
        pos -= 1;
        if (pos <= 0) {
          deleting = false;
          line = (line + 1) % lines.length;
        }
      } else {
        pos += 1;
      }
      setText(lines[line].slice(0, Math.max(pos, 0)));
      if (!deleting && pos >= lines[line].length) {
        deleting = true;
        timer = setTimeout(tick, 2200);
        return;
      }
      timer = setTimeout(tick, deleting ? 22 : 55);
    };
    timer = setTimeout(tick, 3000);
    return () => clearTimeout(timer);
  }, [lines, active]);
  return text;
}

// Default copy of the brand demo. The examples gallery passes its own (English or Turkish).
export const PHONE_COPY = {
  learnedFrom: "Learned from",
  askUs: "Ask us anything",
  online: "online",
  tryAsking: "Try asking",
  askPlaceholder: (name) => `Ask ${name} a question…`,
  tryHint: "Try it — type your own question",
  composerLabel: "Ask a question",
  send: "Send",
  noAnswer: "I could not answer that.",
  tooMany: "Too many questions in a minute. Try again shortly.",
  limitReached:
    "You have used this example's questions for today. Build an assistant like this from your own website: your first 5 qualified prospects are free.",
  limitCta: "Build mine from my website",
  powered: "Powered by Sonic Prospect",
};

function Bubble({ role, text, sources, time, copy = PHONE_COPY, plainSources = false }) {
  return (
    <div className={cx("msg", role)}>
      {role === "bot" && <img className={cx("msg-mark")} src={`${BRAND}/icon-dark@2x.png`} alt="" />}
      <div className={cx("bubble", role)}>
        {text}
        {(sources || []).filter(Boolean).map((source) =>
          plainSources ? (
            <span key={source} className={cx("source")}>
              {copy.learnedFrom}: {sourceLabel(source)}
            </span>
          ) : (
            <a key={source} className={cx("source")} href={source} target="_blank" rel="noopener noreferrer">
              {copy.learnedFrom}: {sourceLabel(source)}
            </a>
          )
        )}
        <span className={cx("time")}>
          {time}
          {role === "user" && <span className={cx("ticks")}> ✓✓</span>}
        </span>
      </div>
    </div>
  );
}

function Typing() {
  return (
    <div className={cx("msg", "bot")}>
      <img className={cx("msg-mark")} src={`${BRAND}/icon-dark@2x.png`} alt="" />
      <div className={cx("bubble", "bot", "typing")} role="status" aria-label="Sonic Prospect is typing">
        <span className={cx("sp-dots")} aria-hidden="true">
          <i />
          <i />
          <i />
        </span>
      </div>
    </div>
  );
}

function Message({ title, children }) {
  return (
    <div className={cx("root")} lang="en">
      <main className={cx("panel", "gate")}>
        <img className={cx("lockup-full")} src={`${BRAND}/lockup-header.png`} alt="Sonic Prospect by Pivony" />
        <h1>{title}</h1>
        {children}
      </main>
    </div>
  );
}

export function Phone({ demo, channel, ask: askFn, copy = PHONE_COPY, plainSources = false, onLimitSignup }) {
  const clock = useClock();
  const [messages, setMessages] = useState([]);
  const [typing, setTyping] = useState(false);
  const [input, setInput] = useState("");
  // Until the visitor engages, the composer pulses so the phone reads as a live chat, not a screenshot.
  const [engaged, setEngaged] = useState(false);
  // Set when the visitor has used this example's answers: the composer becomes a sign-up button.
  const [limit, setLimit] = useState(null);
  const logRef = useRef(null);
  const examples = useMemo(
    () => [copy.askPlaceholder(demo.name), ...(demo.questions || []).slice(1)],
    [copy, demo.name, demo.questions]
  );
  const historyRef = useRef([]);
  const placeholder = useTypedPlaceholder(examples, !engaged && !input);
  const hinting = !engaged && messages.some((row, index) => index > 0 && row.role === "bot");
  const channelRef = useRef(channel);
  channelRef.current = channel;

  // ``auto``: the first question, asked for the visitor when the phone opens.
  const ask = useCallback(
    async (question, auto = false) => {
      const text = (question || "").trim();
      if (!text) return;
      setMessages((rows) => [...rows, { role: "user", text, time: stamp() }]);
      setTyping(true);
      const history = historyRef.current;
      const { ok, status, body } = askFn
        ? await askFn(text, channelRef.current, history, auto)
        : await askDemo(demo.slug, text, channelRef.current);
      setTyping(false);
      let answer = body.answer || copy.noAnswer;
      if (status === 429 && body.error === "visitor_limit") {
        answer = copy.limitReached || copy.tooMany;
        setLimit({ signupUrl: body.signup_url || demo.signup_url || PRODUCT_URL });
      } else if (status === 429) answer = copy.tooMany;
      else if (!ok && status !== 200) answer = copy.noAnswer;
      if (ok && body.answer) {
        // The API's signature on its answer goes back with the history (example demos check it).
        const answerTurn = { role: "assistant", content: body.answer, ...(body.sig ? { sig: body.sig } : {}) };
        historyRef.current = [...history, { role: "user", content: text }, answerTurn].slice(-8);
      }
      setMessages((rows) => [...rows, { role: "bot", text: answer, sources: body.sources || [], time: stamp() }]);
    },
    [demo.slug, demo.signup_url, askFn, copy]
  );

  useEffect(() => {
    historyRef.current = [];
    setLimit(null);
    setMessages([{ role: "bot", text: demo.greeting, time: stamp() }]);
    if (demo.questions && demo.questions[0]) ask(demo.questions[0], true);
  }, [demo.greeting, demo.questions, ask]);

  useEffect(() => {
    if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight;
  }, [messages, typing]);

  const handle = `@${demo.slug}`;
  const icon = (variant) => <img src={`${BRAND}/icon-${variant}@2x.png`} alt="" />;
  return (
    <div className={cx("phone")} data-channel={channel}>
      <div className={cx("notch")} />
      <div className={cx("screen")}>
        <div className={cx("status")}>
          <span>{clock}</span>
        </div>
        <div className={cx("head", "head-website")}>
          <span className={cx("back")}>‹</span>
          <span className={cx("avatar", "web")}>{icon("dark")}</span>
          <span className={cx("who")}>
            <b>{demo.name}</b>
            <small>{copy.askUs}</small>
          </span>
        </div>
        <div className={cx("head", "head-instagram")}>
          <span className={cx("back")}>‹</span>
          <span className={cx("avatar", "ig")}>
            <span>{icon("dark")}</span>
          </span>
          <span className={cx("who")}>
            <b>{demo.name}</b>
            <small>{handle}</small>
          </span>
        </div>
        <div className={cx("head", "head-whatsapp")}>
          <span className={cx("back")}>‹</span>
          <span className={cx("avatar", "wa")}>{icon("white")}</span>
          <span className={cx("who")}>
            <b>{demo.name}</b>
            <small>{copy.online}</small>
          </span>
        </div>
        <div className={cx("chat", hinting && "has-hint")} ref={logRef}>
          {messages.map((row, index) => (
            <Bubble key={index} {...row} copy={copy} plainSources={plainSources} />
          ))}
          {typing && <Typing />}
          {!limit && (demo.questions || []).length > 1 && (
            <div className={cx("chips")}>
              <span className={cx("chips-label")}>{copy.tryAsking}</span>
              {demo.questions.slice(1).map((question) => (
                <button
                  type="button"
                  key={question}
                  className={cx("chip")}
                  onClick={() => {
                    setEngaged(true);
                    ask(question);
                  }}
                >
                  {question}
                </button>
              ))}
            </div>
          )}
        </div>
        {limit ? (
          <div className={cx("composer", "limit-cta")}>
            <a href={limit.signupUrl} target="_blank" rel="noopener noreferrer" onClick={onLimitSignup}>
              {copy.limitCta || PHONE_COPY.limitCta}
            </a>
          </div>
        ) : (
          <form
            className={cx("composer", !engaged && "is-idle")}
            onSubmit={(event) => {
              event.preventDefault();
              ask(input);
              setInput("");
            }}
          >
            <input
              value={input}
              onChange={(event) => setInput(event.target.value)}
              onFocus={() => setEngaged(true)}
              placeholder={placeholder}
              autoComplete="off"
              aria-label={copy.composerLabel}
            />
            <button type="submit" aria-label={copy.send}>
              ➤
            </button>
          </form>
        )}
        <a className={cx("powered")} href={PRODUCT_URL} target="_blank" rel="noopener noreferrer">
          {copy.powered}
        </a>
      </div>
      {hinting && (
        <div className={cx("try-hint")} aria-hidden="true">
          {copy.tryHint}
        </div>
      )}
    </div>
  );
}

function Pricing({ slug, startUrl }) {
  const [open, setOpen] = useState(false);
  const tracked = useRef(false);
  const toggle = () => {
    setOpen((value) => !value);
    if (!open && !tracked.current) {
      tracked.current = true;
      trackDemo(slug, "pricing_open");
    }
  };
  return (
    <div className={cx("offer")}>
      <button
        type="button"
        className={cx("btn", "p", "price-toggle")}
        aria-expanded={open ? "true" : "false"}
        aria-controls="price-panel"
        onClick={toggle}
      >
        How much does it cost? <span className={cx("chev")} aria-hidden="true" />
      </button>
      <div className={cx("price-wrap", open && "is-open")} id="price-panel" inert={open ? undefined : ""}>
        <div className={cx("price-panel")}>
          <div className={cx("price-body")}>
            <p className={cx("price-label")}>{OFFER.label}</p>
            <p className={cx("price-big")}>{OFFER.priceLine}</p>
            <p className={cx("price-sub")}>{OFFER.subline}</p>
            <p className={cx("price-def")}>{OFFER.definition}</p>
            <p className={cx("price-note")}>{OFFER.note}</p>
            <div className={cx("price-actions")}>
              <a
                className={cx("btn", "s")}
                href={campaignUrl("https://pivony.com/plans#prospect", slug)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackDemo(slug, "pricing_click")}
              >
                See full pricing
              </a>
              <a
                className={cx("btn", "p")}
                href={startUrl}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackDemo(slug, "start")}
              >
                Start now
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

const ProspectDemo = ({ NotFound }) => {
  const { slug } = useParams();
  const [state, setState] = useState({ loading: true });
  const [channel, setChannel] = useState("website");

  useEffect(() => {
    let alive = true;
    fetchDemo(slug).then(({ status, body }) => {
      if (alive) setState({ loading: false, status, demo: body });
    });
    return () => {
      alive = false;
    };
  }, [slug]);

  const demo = state.demo || {};
  useEffect(() => {
    document.title = demo.name ? `Prepared for ${demo.name} by Pivony` : "Sonic Prospect demo";
  }, [demo.name]);

  if (state.loading) return <div className={cx("root")} lang="en" />;
  if (state.status === 404) {
    const missing = (
      <Message title="This demo is not available.">
        <p>The link may be mistyped, or the demo was removed.</p>
      </Message>
    );
    return NotFound ? <NotFound missing={missing} /> : missing;
  }
  if (state.status !== 200) {
    return (
      <Message title="This demo is temporarily unavailable.">
        <p>Please try again in a few minutes.</p>
      </Message>
    );
  }
  if (demo.state === "claimed") {
    return (
      <Message title={`${demo.name}'s assistant is live.`}>
        <p>This demo is now part of {demo.name}'s Pivony account.</p>
        <a className={cx("btn", "p")} href="/login">
          Sign in to Pivony
        </a>
      </Message>
    );
  }
  if (demo.state === "expired") {
    return (
      <Message title={`The demo for ${demo.name} has ended.`}>
        <p>Talk to us to see it again or to set up your assistant.</p>
        <a className={cx("btn", "p")} href={campaignUrl(PRODUCT_URL, slug, "expired")}>
          Book a call with Pivony
        </a>
      </Message>
    );
  }

  const logo = demo.logo_url ? (
    <img className={cx("brand-logo")} src={demo.logo_url} alt={demo.name} />
  ) : (
    <div className={cx("brand-word")}>{demo.name}</div>
  );
  const start = () => trackDemo(slug, "start");
  return (
    <div className={cx("root")} lang="en">
      <div className={cx("demo")}>
        <header className={cx("topbar")}>
          <div>
            <p className={cx("prep")}>Prepared for {demo.name} by Pivony</p>
            {logo}
          </div>
          <img
            className={cx("sp-lockup")}
            src={`${BRAND}/lockup-white@2x.png`}
            srcSet={`${BRAND}/lockup-white.png 1x, ${BRAND}/lockup-white@2x.png 2x`}
            alt="Sonic Prospect by Pivony"
          />
        </header>
        <main className={cx("layout")}>
          <section className={cx("left")}>
            <p className={cx("live")}>
              <span className={cx("live-dot")} aria-hidden="true" />
              <b>Live demo</b> · Ask anything about {demo.name}
            </p>
            <div className={cx("tabs")} role="tablist">
              {CHANNELS.map((item) => (
                <button
                  type="button"
                  key={item.id}
                  role="tab"
                  aria-selected={channel === item.id ? "true" : "false"}
                  className={cx("tab", channel === item.id && "is-on")}
                  onClick={() => setChannel(item.id)}
                >
                  {item.label}
                </button>
              ))}
            </div>
            <Phone demo={demo} channel={channel} />
          </section>
          <section className={cx("right")}>
            <h1>Your assistant already knows {demo.name}.</h1>
            <p className={cx("lead")}>
              We trained Sonic on your site so you can see how it answers your visitors on your website, Instagram,
              and WhatsApp.
            </p>
            <div className={cx("card")}>
              <h3>Read {demo.page_count} pages from your site</h3>
              <div className={cx("tags")}>
                {(demo.topics || []).map((topic) => (
                  <span key={topic}>{topic}</span>
                ))}
              </div>
            </div>
            <div className={cx("card")}>
              <h3>What your assistant can do today</h3>
              <div className={cx("ben")}>
                <span className={cx("ic")} aria-hidden="true">
                  ◷
                </span>
                Answers visitors 24/7
              </div>
              <div className={cx("ben")}>
                <span className={cx("ic")} aria-hidden="true">
                  ✦
                </span>
                {demo.steer}
              </div>
              <div className={cx("ben")}>
                <span className={cx("ic")} aria-hidden="true">
                  ⇄
                </span>
                Same assistant on your website, Instagram and WhatsApp
              </div>
            </div>
            <div className={cx("actions")}>
              <Pricing slug={slug} startUrl={demo.signup_url} />
              <a className={cx("btn", "p")} href={demo.signup_url} target="_blank" rel="noopener noreferrer" onClick={start}>
                Start now
              </a>
              {panelKey(slug) && (
                <a className={cx("btn", "p")} href={`/demos/${encodeURIComponent(slug)}/panel`}>
                  Open your panel
                </a>
              )}
              <a
                className={cx("btn", "s")}
                href={campaignUrl(PRODUCT_URL, slug)}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackDemo(slug, "book_call")}
              >
                Book a call with Pivony
              </a>
            </div>
          </section>
        </main>
      </div>
    </div>
  );
};

export default ProspectDemo;
